/*
 * A-Eco Pond Health Advisor — recommendation engine (pure logic, no DOM).
 * Loaded in the theme as a browser global (window.PondAdvisorEngine) and also
 * requireable in Node for tests (UMD footer at the bottom).
 *
 * DESIGN RULES (read before editing):
 *  - This file NEVER invents product dosing, prices, inventory, or disease
 *    diagnoses. It only decides which *categories* of help are justified and
 *    explains why in the customer's own numbers. The section maps categories to
 *    real Shopify products.
 *  - Water-quality thresholds live in RULES so they can be tuned without
 *    touching the analysis code.
 *  - Ammonia, pH and temperature are evaluated in RELATION to each other
 *    (un-ionized ammonia rises with pH and temperature), never in isolation.
 *  - When data is missing, say so instead of guessing.
 */
(function (root, factory) {
  if (typeof module === 'object' && module.exports) module.exports = factory();
  else root.PondAdvisorEngine = factory();
})(typeof self !== 'undefined' ? self : this, function () {
  'use strict';

  // ---- Editable thresholds (general pond-care guidance, not label doses) ----
  var RULES = {
    ph: { min: 6.5, max: 8.5, note: 'Most pond fish are comfortable between 6.5 and 8.5. Stability matters more than hitting an exact number.' },
    ammonia: { target: 0, watch: 0.25, note: 'The goal for ammonia is 0 ppm. Any reading above 0 stresses fish.' },
    nitrite: { target: 0, watch: 0.25, note: 'The goal for nitrite is 0 ppm. Nitrite interferes with a fish’s ability to carry oxygen.' },
    nitrate: { good: 40, high: 80, note: 'Under about 40 ppm is a common target. Higher nitrate is rarely an acute emergency but feeds algae.' },
    kh: { low: 90, good: 150, crash: 70, note: 'KH (carbonate hardness) buffers pH; the ideal range is about 90–150 ppm. Below ~90 pH starts to drift and below ~70 a pH crash becomes likely.' },
    oxygen: { low: 5, good: 6, note: 'Fish generally do well above about 6 mg/L dissolved oxygen. Warm water holds less oxygen than cool water.' },
    temp: { cold: 50, warm: 75, hot: 82, note: 'Water temperature changes oxygen capacity, fish metabolism, and how toxic ammonia becomes.' }
  };

  // Product CATEGORIES the engine can justify. The theme maps these to real
  // products via `advisor:<key>` tags / product_type / title keywords, and
  // prioritizes EasyPro (A-Eco's core maintenance line) where available.
  //   subscription: true -> eligible for the recurring Seasonal Care plan
  //                         (preventative maintenance only, never medications).
  //   oneTime: true      -> corrective / entry-point item, never recurring.
  var CATEGORIES = {
    // Entry point + corrective — NEVER part of the recurring subscription.
    testKit: { key: 'test-kit', label: 'Test kit', keywords: ['test kit', 'master test', 'liquid test'], type: 'test', oneTime: true },
    conditioner: { key: 'water-conditioner', label: 'Water conditioner', keywords: ['dechlorinator', 'conditioner', 'chlorine'], type: 'water treatment', oneTime: true },
    salt: { key: 'salt', label: 'Pond salt', keywords: ['pond salt'], type: 'water treatment', oneTime: true },
    uvClarifier: { key: 'uv-clarifier', label: 'UV clarifier', keywords: ['uv clarifier', 'ultraviolet clarifier', 'uv sterilizer', 'uvc ', 'ultra violet'], type: 'clarifier', oneTime: true },
    // Preventative maintenance — eligible for the EasyPro Seasonal Care subscription.
    bacteria: { key: 'bacteria', label: 'Beneficial bacteria', keywords: ['beneficial bacteria', 'bacteria', 'nitrifying', 'defense'], type: 'water treatment', subscription: true },
    coldBacteria: { key: 'cold-bacteria', label: 'Cold-water bacteria', keywords: ['cold water bacteria', 'cold-water bacteria', 'cold weather bacteria'], type: 'water treatment', subscription: true },
    sludgeReducer: { key: 'sludge-reducer', label: 'Sludge & muck reducer', keywords: ['sludge', 'muck', 'pellet', 'digest'], type: 'water treatment', subscription: true },
    clarifier: { key: 'clarifier', label: 'Water clarifier', keywords: ['clarifier', 'barley', 'flocculant'], type: 'water treatment', subscription: true },
    khBuffer: { key: 'kh-buffer', label: 'KH / alkalinity buffer', keywords: ['kh buffer', 'alkalinity', 'carbonate hardness', 'buffer'], type: 'water treatment', subscription: true },
    filterMedia: { key: 'filter-media', label: 'Filter media & pads', keywords: ['filter media', 'filter pad', 'filter mat', 'bio media', 'matala', 'brush'], type: 'filtration', subscription: true },
    aeration: { key: 'aeration', label: 'Aeration', keywords: ['aeration', 'aerator', 'air pump', 'diffuser', 'de-icer', 'deicer'], type: 'aeration', subscription: true }
  };

  var num = function (v) {
    if (v === null || v === undefined || v === '') return null;
    var n = parseFloat(v);
    return isNaN(n) ? null : n;
  };

  function estimateGallons(input) {
    var known = num(input.knownGallons);
    if (known && known > 0) return { gallons: Math.round(known), estimated: false };
    var l = num(input.length), w = num(input.width), d = num(input.depth);
    if (l && w && d && l > 0 && w > 0 && d > 0) {
      // length x width x avg depth (ft) x 7.48 gal/ft^3, ~85% for irregular shape/shelves
      return { gallons: Math.round(l * w * d * 7.48 * 0.85), estimated: true };
    }
    return { gallons: null, estimated: false };
  }

  // Fraction of total ammonia that is toxic un-ionized NH3, from pH & temp.
  // Used only to gauge RELATIVE urgency, never shown as a dosing figure.
  function unIonizedFraction(ph, tempF) {
    if (ph === null) return null;
    var t = tempF === null ? 68 : tempF;
    var tempC = (t - 32) * 5 / 9;
    var pKa = 0.09018 + 2729.92 / (tempC + 273.15);
    return 1 / (1 + Math.pow(10, pKa - ph));
  }

  function analyze(input, opts) {
    opts = opts || {};
    var g = estimateGallons(input);
    var gallons = g.gallons;
    var ph = num(input.ph), ammonia = num(input.ammonia), nitrite = num(input.nitrite),
        nitrate = num(input.nitrate), kh = num(input.kh), oxygen = num(input.oxygen),
        temp = num(input.waterTemp);
    var chlorine = input.chlorine || '';
    var fish = input.fishLoad || '';
    var symptoms = Array.isArray(input.symptoms) ? input.symptoms : (input.symptoms ? [input.symptoms] : []);
    var isKoi = /koi|heavy/i.test(fish);
    var hasFish = fish && fish !== 'No fish';
    var tested = [ph, ammonia, nitrite, nitrate, kh, oxygen].some(function (v) { return v !== null; });

    var readouts = [];
    var advisories = [];
    var needed = {}; // category key -> reason
    var need = function (cat, reason) { if (!needed[cat.key]) needed[cat.key] = { category: cat, reason: reason }; };
    var flags = [];

    // ---------- Per-parameter readouts (education) ----------
    if (ph !== null) {
      var phBand = (ph < RULES.ph.min || ph > RULES.ph.max) ? 'watch' : 'good';
      var phMsg = RULES.ph.note;
      if (kh !== null && kh < RULES.kh.low) phMsg += ' Your KH is low, so this pH can move fast — change it gradually.';
      readouts.push({ name: 'pH', value: ph.toFixed(1), band: phBand, text: phMsg });
    }
    if (ammonia !== null) {
      var aBand = ammonia > RULES.ammonia.watch ? 'act' : (ammonia > RULES.ammonia.target ? 'watch' : 'good');
      var frac = unIonizedFraction(ph, temp);
      var aMsg = ammonia > 0
        ? 'Detected at ' + ammonia + ' ppm. The target is 0.'
        : RULES.ammonia.note;
      if (ammonia > 0 && frac !== null && (ph !== null && ph >= 8) && (temp === null || temp >= 68)) {
        aMsg += ' Because your pH is on the higher side' + (temp !== null ? ' and the water is warm' : '') + ', more of this ammonia is in its more toxic form — treat it with added urgency.';
      } else if (ammonia > 0 && ph === null) {
        aMsg += ' Ammonia toxicity depends on pH and temperature, which you haven’t entered — test those too.';
      }
      readouts.push({ name: 'Ammonia', value: ammonia + ' ppm', band: aBand, text: aMsg });
    }
    if (nitrite !== null) {
      var nBand = nitrite > RULES.nitrite.watch ? 'act' : (nitrite > RULES.nitrite.target ? 'watch' : 'good');
      readouts.push({ name: 'Nitrite', value: nitrite + ' ppm', band: nBand, text: nitrite > 0 ? 'Detected at ' + nitrite + ' ppm. The target is 0 — nitrite usually means the biofilter is still catching up.' : RULES.nitrite.note });
    }
    if (nitrate !== null) {
      var ntBand = nitrate > RULES.nitrate.high ? 'act' : (nitrate > RULES.nitrate.good ? 'watch' : 'good');
      readouts.push({ name: 'Nitrate', value: nitrate + ' ppm', band: ntBand, text: nitrate > RULES.nitrate.good ? 'At ' + nitrate + ' ppm this is above the ~40 ppm comfort target and can feed algae. Partial water changes and lighter feeding bring it down.' : RULES.nitrate.note });
    }
    if (kh !== null) {
      var kBand = kh < RULES.kh.low ? 'watch' : 'good';
      readouts.push({ name: 'KH', value: kh + ' ppm', band: kBand, text: kh < RULES.kh.low ? 'At ' + kh + ' ppm your buffer is low, which is the usual root cause of pH swings. Raise it gradually.' : RULES.kh.note });
    }
    if (oxygen !== null) {
      var oBand = oxygen < RULES.oxygen.low ? 'act' : (oxygen < RULES.oxygen.good ? 'watch' : 'good');
      var oMsg = oxygen < RULES.oxygen.good ? 'At ' + oxygen + ' mg/L, oxygen is on the low side.' : RULES.oxygen.note;
      if (temp !== null && temp >= RULES.temp.warm) oMsg += ' Warm water (' + temp + '°F) holds less oxygen and demand peaks at night.';
      readouts.push({ name: 'Dissolved O₂', value: oxygen + ' mg/L', band: oBand, text: oMsg });
    }
    if (temp !== null) {
      var tBand = temp >= RULES.temp.hot ? 'watch' : 'good';
      var tMsg = RULES.temp.note;
      if (temp >= RULES.temp.hot) tMsg += ' At ' + temp + '°F, oxygen capacity drops and ammonia becomes more toxic — watch fish closely.';
      readouts.push({ name: 'Temp', value: temp + '°F', band: tBand, text: tMsg });
    }

    // ---------- Emergency / symptom flags (never a diagnosis) ----------
    var gasping = symptoms.indexOf('Fish gasping') > -1;
    var losses = symptoms.indexOf('Dead fish') > -1;

    if (gasping || losses) {
      flags.push('Fish distress reported');
      advisories.push({ tier: 'CRITICAL', title: 'Fish are in distress — stabilize before anything else', points: [
        'Increase aeration immediately (air pump/diffuser, or aim a fountain/waterfall at the surface). More surface movement is one of the safest first moves and buys time.',
        'Do a partial water change with dechlorinated water if ammonia, nitrite, or chlorine may be present. Stop or cut feeding until things stabilize.',
        oxygen !== null && oxygen < RULES.oxygen.low ? 'Your oxygen reading (' + oxygen + ' mg/L) is low and consistent with gasping.' : 'Gasping at the surface often points to low oxygen or a water-quality problem, not necessarily disease.',
        'This tool cannot diagnose illness. If fish keep dying or symptoms persist after water quality is corrected, contact a qualified aquatic vet or pond professional.'
      ].filter(Boolean) });
      need(CATEGORIES.aeration, 'Fish distress reported — added aeration is a safe first response while you correct water quality.');
    }

    if (chlorine === 'Present / suspected') {
      flags.push('Chlorine/chloramine suspected');
      advisories.push({ tier: 'URGENT', title: 'Chlorine or chloramine may be present', points: [
        'Chlorine and chloramine from municipal water are directly harmful to fish and to the beneficial bacteria in your filter.',
        'Any time you add tap water, treat it with a pond-safe conditioner that neutralizes both chlorine AND chloramine. Follow the product label for the amount — confirm your pond volume first.',
        gallons ? 'Your pond is about ' + gallons.toLocaleString() + ' gallons' + (g.estimated ? ' (estimated)' : '') + ', so dose for that volume — under-dosing leaves chlorine active.' : 'You haven’t confirmed your pond volume yet, so measure it before dosing.'
      ] });
      need(CATEGORIES.conditioner, 'Chlorine/chloramine suspected — a conditioner neutralizes it and protects your filter bacteria.');
    }

    // ---------- Water chemistry advisories ----------
    if (ammonia !== null && ammonia > 0) {
      flags.push('Ammonia ' + ammonia + ' ppm');
      var aPts = [
        'The first fix for ammonia is environmental, not a bottle: stop or reduce feeding, check that your filter is running and not clogged, and do a partial water change with dechlorinated water.',
        'Do not do a full clean-out — it can crash the biofilter and make ammonia worse.',
        'Re-test in 24 hours. Ammonia during a new pond or after a filter disruption usually clears as bacteria catch up.'
      ];
      if (ph !== null && ph >= 8) aPts.push('Your higher pH makes this ammonia more toxic, so act sooner rather than waiting.');
      if (hasFish) aPts.push('Extra aeration helps fish cope with the stress while you correct the cause.');
      advisories.push({ tier: ammonia > RULES.ammonia.watch ? 'URGENT' : 'WARNING', title: 'Ammonia detected — correct the environment first', points: aPts });
      need(CATEGORIES.bacteria, 'Ammonia is present' + (symptoms.indexOf('Odor sludge') > -1 ? ' along with odor/sludge' : '') + ' — beneficial bacteria support the biological processing of waste. Correct feeding and filtration first.');
      if (gasping || losses || ph !== null && ph >= 8) need(CATEGORIES.aeration, 'Ammonia is more stressful to fish when pH or temperature is high — aeration helps them cope.');
    }

    if (nitrite !== null && nitrite > 0) {
      flags.push('Nitrite ' + nitrite + ' ppm');
      advisories.push({ tier: 'WARNING', title: 'Nitrite detected — biofilter still maturing', points: [
        'Nitrite above 0 means your biological filter hasn’t fully caught up. Support it: keep the filter running 24/7, avoid heavy cleaning, and feed lightly.',
        'Partial water changes with dechlorinated water lower nitrite while bacteria establish.',
        'Good oxygen and water movement help fish tolerate nitrite. Re-test every couple of days until it holds at 0.'
      ] });
      need(CATEGORIES.bacteria, 'Nitrite present — beneficial bacteria are what convert it to safer nitrate; supporting the colony is the direct fix.');
    }

    if (ph !== null && kh !== null && kh < RULES.kh.low && (ph < RULES.ph.min || ph > RULES.ph.max)) {
      advisories.push({ tier: 'WARNING', title: 'Unstable pH from low KH — correct slowly', points: [
        'Your pH is outside the comfort band and your KH is low (' + kh + ' ppm), which is the usual cause of pH swings.',
        'Raise KH gradually over several days and re-test after each addition. Never try to correct pH all at once — a fast swing is more dangerous to fish than the current reading.',
        'Confirm your pond volume before adding any buffer so you don’t overshoot. Follow the product label.'
      ] });
      need(CATEGORIES.khBuffer, 'Low KH (' + kh + ' ppm) is destabilizing your pH — a buffer raises KH so pH holds steady. Dose gradually per the label.');
    } else if (kh !== null && kh < RULES.kh.low) {
      advisories.push({ tier: 'INFO', title: 'Low KH — build in a buffer', points: [
        'KH at ' + kh + ' ppm is below the ~90–150 ppm ideal. Even if pH looks fine now, low KH lets it drift, especially overnight.',
        'Raise it gradually and re-test. Confirm your volume first.'
      ] });
      need(CATEGORIES.khBuffer, 'KH is low (' + kh + ' ppm), which risks pH instability — a buffer raises it gradually.');
    }

    if (oxygen !== null && oxygen < RULES.oxygen.low && !(gasping || losses)) {
      advisories.push({ tier: 'WARNING', title: 'Low dissolved oxygen', points: [
        'At ' + oxygen + ' mg/L, oxygen is low enough to stress fish. Add surface movement or aeration.',
        temp !== null && temp >= RULES.temp.warm ? 'Warm water and warm nights are the most common cause this time of year.' : 'Oxygen dips overnight and in warm weather — aeration keeps it steady around the clock.',
        'Reduce feeding and avoid stacking treatments while oxygen is low.'
      ] });
      need(CATEGORIES.aeration, 'Measured oxygen is low (' + oxygen + ' mg/L) — aeration is the direct, low-risk correction.');
    }

    if (nitrate !== null && nitrate > RULES.nitrate.good) {
      advisories.push({ tier: 'INFO', title: 'Elevated nitrate — nutrient, not emergency', points: [
        'Nitrate at ' + nitrate + ' ppm is above the ~40 ppm comfort target. It’s rarely an acute danger but it fuels algae.',
        'Bring it down with partial water changes, lighter feeding, removing debris/sludge, and adding aquatic plants that compete for nutrients.'
      ] });
    }

    // Possible-disease but water not tested
    if ((symptoms.indexOf('Dead fish') > -1 || opts.suspectsDisease) && !tested) {
      advisories.push({ tier: 'URGENT', title: 'Test your water before assuming illness', points: [
        'Most sudden pond problems trace back to water quality, not disease. You haven’t entered test results yet.',
        'Test ammonia, nitrite, pH, and KH first with a liquid test kit. Correcting water quality resolves a large share of symptoms.',
        'If water tests come back clean and symptoms continue, seek a qualified aquatic vet or pond professional rather than guessing at medications.'
      ] });
      need(CATEGORIES.testKit, 'Symptoms without test data — confirming ammonia, nitrite, pH and KH is the responsible first step before treating anything.');
    } else if (flags.length && !tested) {
      need(CATEGORIES.testKit, 'You reported a concern but haven’t confirmed numbers — a full liquid test kit tells you what’s actually happening before you spend on treatments.');
    }

    // Algae (nutrient guidance, cautious)
    if (symptoms.indexOf('Green water') > -1 || symptoms.indexOf('String algae') > -1) {
      advisories.push({ tier: 'INFO', title: 'Algae — treat the cause, not just the symptom', points: [
        'Algae is a nutrient and light problem. Feed less, remove debris and sludge, and add competing plants before reaching for any algaecide.',
        nitrate !== null && nitrate > RULES.nitrate.good ? 'Your nitrate (' + nitrate + ' ppm) is elevated and directly feeds algae — lowering it helps most.' : 'Excess nutrients feed algae even when the water looks otherwise fine.',
        'Only consider a labeled treatment when fish are stable, oxygen is strong, and ammonia/nitrite are at 0 — and always fix the nutrient source or it returns.'
      ] });
      // Green (pea-soup) water is suspended single-cell algae that a UV clarifier clears; string algae is not, so only justify UV for green water.
      if (symptoms.indexOf('Green water') > -1) {
        need(CATEGORIES.uvClarifier, 'Green (pea-soup) water is suspended single-cell algae, which a correctly sized UV clarifier clears reliably. Use it alongside the nutrient control above so the bloom does not simply return — the UV treats the symptom, not the cause.');
      }
    }

    // ---------- Healthy / stable fallback ----------
    var anyConcern = advisories.some(function (a) { return a.tier === 'CRITICAL' || a.tier === 'URGENT' || a.tier === 'WARNING'; });
    if (!anyConcern) {
      advisories.push({ tier: 'INFO', title: tested ? 'No red flags in what you entered' : 'Looking good so far', points: [
        tested ? 'Your entered readings are within comfortable ranges. Keep doing what’s working.' : 'Nothing you entered raises a safety flag — enter water-test numbers for a fuller picture.',
        'Test monthly (ammonia, nitrite, nitrate, pH, KH) so you catch a trend early — that’s far cheaper than treating a crisis.',
        'Feed only what fish finish in a few minutes; overfeeding is the biggest driver of ammonia, sludge, and algae.',
        'Keep aeration steady in summer heat, and rinse filter media in pond water (never tap water) so you don’t kill the bacteria.'
      ] });
    }

    // ---------- Data completeness note ----------
    var missing = [];
    if (ph === null) missing.push('pH');
    if (ammonia === null) missing.push('ammonia');
    if (nitrite === null) missing.push('nitrite');
    if (kh === null) missing.push('KH');
    if (!gallons) missing.push('pond volume');

    // ---------- Care path recommendation ----------
    var carePath = recommendCarePath({ gallons: gallons, isKoi: isKoi, fish: fish });

    return {
      gallons: gallons,
      gallonsEstimated: g.estimated,
      tested: tested,
      readouts: readouts,
      advisories: advisories,
      neededCategories: Object.keys(needed).map(function (k) { return needed[k]; }),
      flags: flags,
      missing: missing,
      carePath: carePath
    };
  }

  function recommendCarePath(o) {
    // Maps to the live PondWise tiers. Recommendation only — not a charge.
    if (o.isKoi || (o.gallons && o.gallons >= 4000)) {
      if (o.gallons && o.gallons >= 8000) return { key: 'estate', reason: 'Large or heavily stocked pond' };
      return { key: 'serious_koi', reason: 'Koi or heavier fish load benefits from stronger seasonal planning' };
    }
    if (o.gallons && o.gallons >= 8000) return { key: 'estate', reason: 'Large pond needs more frequent planning' };
    return { key: 'water_garden', reason: 'Smaller water garden with lighter needs' };
  }

  // Builds the personalized A-Eco Seasonal Care subscription: preventative
  // maintenance ONLY, prioritizing EasyPro products. It never adds a test kit
  // (that is the one-time entry point) and never adds medications. Selection is
  // driven by pond size, current season, filtration, fish load, and the
  // customer's stated maintenance goals. Categories here are mapped to real
  // EasyPro products by the theme; missing SKUs fall back to a shop link.
  function buildSubscription(input, analysis) {
    analysis = analysis || analyze(input);
    var gallons = analysis.gallons;
    var season = normalizeSeason(input.season);
    var temp = num(input.waterTemp);
    var kh = num(input.kh);
    var fish = input.fishLoad || '';
    var isKoi = /koi|heavy/i.test(fish);
    var hasFish = fish && fish !== 'No fish';
    var filtration = (input.filtration || '').toLowerCase();
    var hasFilter = filtration && filtration.indexOf('none') === -1 && filtration.indexOf('not sure') === -1;
    var sun = (input.sun || '').toLowerCase();
    var goals = Array.isArray(input.maintenanceGoals) ? input.maintenanceGoals : (input.maintenanceGoals ? [input.maintenanceGoals] : []);
    var symptoms = Array.isArray(input.symptoms) ? input.symptoms : (input.symptoms ? [input.symptoms] : []);
    var wantsGoal = function (g) { return goals.some(function (x) { return String(x).toLowerCase().indexOf(g) > -1; }); };
    var hasSymptom = function (s) { return symptoms.indexOf(s) > -1; };
    var dormant = (temp !== null && temp < RULES.temp.cold) || season === 'Winter';

    var components = [];
    var add = function (category, cadence, reason) { components.push({ category: category, cadence: cadence, reason: reason }); };

    // 1. Biological support — the backbone of every plan. Cold-water strain in fall/dormancy.
    if (season === 'Fall' || dormant) {
      add(CATEGORIES.coldBacteria, dormant ? 'Until water drops below ~50°F, then pause' : 'Monthly through fall',
        'A cold-water bacteria blend keeps waste breaking down as the pond cools, so spring starts clean.');
    } else {
      add(CATEGORIES.bacteria, 'Monthly in the active season',
        'Beneficial bacteria keep the biofilter ahead of fish waste — the single best preventative habit for a healthy pond.');
    }

    // 2. Sludge / muck — bottom organics from fish load, size, or visible sludge.
    if (hasSymptom('Odor sludge') || isKoi || (gallons && gallons >= 2000) || wantsGoal('sludge') || wantsGoal('muck')) {
      add(CATEGORIES.sludgeReducer, 'Monthly in warm months',
        hasSymptom('Odor sludge') ? 'You reported odor/sludge — a muck reducer digests bottom organics before they drive ammonia and odor.'
          : 'Your pond size and fish load build bottom muck over a season; a reducer keeps it in check preventatively.');
    }

    // 3. Clarity — algae pressure from sun, nutrients, or a stated clarity goal.
    if (hasSymptom('Green water') || hasSymptom('String algae') || sun.indexOf('full') > -1 || wantsGoal('clear') || wantsGoal('algae')) {
      add(CATEGORIES.clarifier, 'As needed in warm months',
        sun.indexOf('full') > -1 ? 'Full sun drives algae — a clarifier plus good habits keeps water clear without reaching for chemicals.'
          : 'To keep water clear, a clarifier addresses the nutrients and fine particles that cloud a pond.');
    }

    // 4. Filtration upkeep — only if they actually run a filter.
    if (hasFilter || wantsGoal('filter')) {
      add(CATEGORIES.filterMedia, 'Rinse routinely, refresh seasonally',
        'Clean, intact media is what lets your filter and its bacteria do their job — replacements keep flow and biology strong.');
    }

    // 5. KH stability — low buffer, or a stated water-balance goal.
    if ((kh !== null && kh < RULES.kh.low) || wantsGoal('ph') || wantsGoal('balance') || wantsGoal('stable')) {
      add(CATEGORIES.khBuffer, 'Monthly — always test first',
        kh !== null ? 'Your KH is low (' + kh + ' ppm), so a buffer on hand prevents the pH swings that stress fish.'
          : 'A buffer keeps KH and pH stable through the season so you never chase a sudden swing.');
    }

    // 6. Aeration — heat, ice, larger water, heavy stock, or a stated goal.
    if (season === 'Summer' || season === 'Winter' || (gallons && gallons >= 4000) || isKoi || wantsGoal('aeration') || wantsGoal('oxygen')) {
      add(CATEGORIES.aeration, season === 'Winter' ? 'Winter (keep a hole in the ice)' : 'Seasonal',
        season === 'Winter' ? 'Winter aeration/de-icing keeps a gas-exchange hole open so the pond does not go anoxic under ice.'
          : 'Steady aeration protects fish through summer heat and low-oxygen nights — preventative, not a treatment.');
    }

    // Safety net: never return an empty plan.
    if (!components.length) {
      add(CATEGORIES.bacteria, 'Monthly in the active season',
        'A monthly bacteria dose is the core preventative habit for any pond.');
    }

    return {
      tier: analysis.carePath ? analysis.carePath.key : recommendCarePath({ gallons: gallons, isKoi: isKoi, fish: fish }).key,
      season: season,
      gallons: gallons,
      hasFish: hasFish,
      components: components,
      excludes: ['test-kit', 'medications'],
      note: 'Preventative maintenance only — this plan never includes fish medications or one-time test kits. Test kits are how you start, not something you subscribe to.'
    };
  }

  function normalizeSeason(s) {
    if (!s) return null;
    s = String(s).toLowerCase();
    if (s.indexOf('spring') > -1) return 'Spring';
    if (s.indexOf('summer') > -1) return 'Summer';
    if (s.indexOf('fall') > -1 || s.indexOf('autumn') > -1) return 'Fall';
    if (s.indexOf('winter') > -1) return 'Winter';
    return null;
  }

  // ================= Reading status model (6-state) =================
  // Centralized reading metadata for both UI and status logic.
  var READINGS = {
    ph:        { name: 'pH', unit: '', target: '6.5–8.5' },
    ammonia:   { name: 'Ammonia', unit: 'ppm', target: '0 ppm' },
    nitrite:   { name: 'Nitrite', unit: 'ppm', target: '0 ppm' },
    nitrate:   { name: 'Nitrate', unit: 'ppm', target: 'under 40 ppm' },
    kh:        { name: 'KH / alkalinity', unit: 'ppm', target: '90–150 ppm' },
    oxygen:    { name: 'Dissolved oxygen', unit: 'mg/L', target: 'above 6 mg/L (7–8 ideal)' },
    waterTemp: { name: 'Water temperature', unit: '°F', target: '65–75°F ideal (~50–85°F ok)' }
  };
  // Plausible input ranges — outside these we say "unable to assess" and ask for a retest.
  var PLAUSIBLE = { ph: [0, 14], ammonia: [0, 20], nitrite: [0, 20], nitrate: [0, 500], kh: [0, 500], oxygen: [0, 25], waterTemp: [32, 110] };
  function plausible(key, x) { var r = PLAUSIBLE[key]; return !r || (x >= r[0] && x <= r[1]); }

  // Returns one status object per reading (entered or not). Pure; never mutates input.
  function computeReadingStatuses(input) {
    var v = { ph: num(input.ph), ammonia: num(input.ammonia), nitrite: num(input.nitrite),
      nitrate: num(input.nitrate), kh: num(input.kh), oxygen: num(input.oxygen), waterTemp: num(input.waterTemp) };
    var hot = v.waterTemp !== null && v.waterTemp >= RULES.temp.warm;
    var highPh = v.ph !== null && v.ph >= 8;
    function mk(key, status, explanation, opts) {
      opts = opts || {};
      var m = READINGS[key];
      return { key: key, name: m.name, unit: m.unit, target: m.target, value: v[key], entered: v[key] !== null,
        status: status, explanation: explanation, retest: !!opts.retest, affects: opts.affects || [] };
    }
    var out = [];
    // pH
    if (v.ph === null) out.push(mk('ph', 'missing', 'Not entered. pH sets how toxic ammonia becomes, so it is worth testing.', { affects: ['ammonia'] }));
    else if (!plausible('ph', v.ph)) out.push(mk('ph', 'unable', 'That reading is outside a pond’s possible range (0–14). Please retest.', { retest: true }));
    else if (v.ph >= 6.5 && v.ph <= 8.5) out.push(mk('ph', 'healthy', 'Within the comfortable range for most pond fish. Stability matters more than the exact number.', { affects: ['ammonia'] }));
    else if ((v.ph >= 6.0 && v.ph < 6.5) || (v.ph > 8.5 && v.ph <= 9.0)) out.push(mk('ph', 'monitor', 'Slightly outside the comfort band. Watch for swings and confirm your KH.', { retest: true, affects: ['ammonia'] }));
    else out.push(mk('ph', 'concerning', 'Outside the comfortable range. Correct gradually — a fast swing is more dangerous than the current value.', { retest: true, affects: ['ammonia'] }));
    // Ammonia
    if (v.ammonia === null) out.push(mk('ammonia', 'missing', 'Not entered. Ammonia is one of the most important safety readings.', { affects: ['oxygen'] }));
    else if (!plausible('ammonia', v.ammonia)) out.push(mk('ammonia', 'unable', 'That value looks unusually high — please retest with a liquid kit.', { retest: true }));
    else if (v.ammonia === 0) out.push(mk('ammonia', 'healthy', 'At the target of 0 ppm. Your biofilter is keeping up with waste.'));
    else if (v.ammonia <= 0.25) out.push(mk('ammonia', 'concerning', 'Any ammonia above 0 stresses fish' + (highPh || hot ? ', and your higher pH/temperature make it more toxic' : '') + '.', { retest: true, affects: ['oxygen'] }));
    else out.push(mk('ammonia', 'critical', 'Elevated ammonia is dangerous' + (highPh || hot ? ' and more toxic at your pH/temperature' : '') + '. Fix the environment first.', { retest: true, affects: ['oxygen'] }));
    // Nitrite
    if (v.nitrite === null) out.push(mk('nitrite', 'missing', 'Not entered. Nitrite shows whether your biofilter is fully cycled.'));
    else if (!plausible('nitrite', v.nitrite)) out.push(mk('nitrite', 'unable', 'That value looks unusually high — please retest.', { retest: true }));
    else if (v.nitrite === 0) out.push(mk('nitrite', 'healthy', 'At the target of 0 ppm.'));
    else if (v.nitrite <= 0.25) out.push(mk('nitrite', 'concerning', 'Nitrite above 0 means the biofilter is still catching up and it interferes with oxygen transport in fish.', { retest: true }));
    else out.push(mk('nitrite', 'critical', 'Elevated nitrite is dangerous to fish — support the biofilter and change water.', { retest: true }));
    // Nitrate
    if (v.nitrate === null) out.push(mk('nitrate', 'missing', 'Not entered. Nitrate is usually low-urgency but drives algae.'));
    else if (!plausible('nitrate', v.nitrate)) out.push(mk('nitrate', 'unable', 'That value looks off — please retest.', { retest: true }));
    else if (v.nitrate <= RULES.nitrate.good) out.push(mk('nitrate', 'healthy', 'Within a comfortable range.'));
    else if (v.nitrate <= RULES.nitrate.high) out.push(mk('nitrate', 'monitor', 'Above the ~40 ppm comfort target — it feeds algae. Water changes and lighter feeding help.'));
    else out.push(mk('nitrate', 'concerning', 'High nitrate fuels algae. Rarely an emergency, but bring it down with water changes and plants.'));
    // KH
    if (v.kh === null) out.push(mk('kh', 'missing', 'Not entered. KH is the buffer that keeps pH stable.', { affects: ['ph'] }));
    else if (!plausible('kh', v.kh)) out.push(mk('kh', 'unable', 'That value looks off — please retest.', { retest: true }));
    else if (v.kh >= RULES.kh.low) out.push(mk('kh', 'healthy', 'In the ideal 90–150 ppm range — enough buffer to keep pH stable.', { affects: ['ph'] }));
    else if (v.kh >= RULES.kh.crash) out.push(mk('kh', 'monitor', 'Below the ideal 90–150 ppm — pH can start to drift, especially overnight. Raise it gradually toward 90–150.', { retest: true, affects: ['ph'] }));
    else out.push(mk('kh', 'concerning', 'Low buffer (under ~70 ppm) is the usual cause of pH swings and crashes. Raise KH slowly and retest.', { retest: true, affects: ['ph'] }));
    // Oxygen
    if (v.oxygen === null) out.push(mk('oxygen', 'missing', 'Not entered. Low oxygen is a common, fixable cause of fish stress.'));
    else if (!plausible('oxygen', v.oxygen)) out.push(mk('oxygen', 'unable', 'That value looks off — please retest.', { retest: true }));
    else if (v.oxygen >= RULES.oxygen.good) out.push(mk('oxygen', 'healthy', 'A comfortable oxygen level for fish.'));
    else if (v.oxygen >= RULES.oxygen.low) out.push(mk('oxygen', 'monitor', 'On the low side' + (hot ? ' — warm water holds less oxygen and it dips overnight' : '') + '. Add surface movement.', { retest: true }));
    else out.push(mk('oxygen', 'concerning', 'Low enough to stress fish. Add aeration or surface movement now.', { retest: true }));
    // Temperature
    if (v.waterTemp === null) out.push(mk('waterTemp', 'missing', 'Not entered. Temperature changes oxygen capacity and ammonia toxicity.', { affects: ['ammonia', 'oxygen'] }));
    else if (!plausible('waterTemp', v.waterTemp)) out.push(mk('waterTemp', 'unable', 'That temperature looks off for a pond — please recheck.', { retest: true }));
    else if (v.waterTemp >= RULES.temp.hot) out.push(mk('waterTemp', 'monitor', 'Warm water holds less oxygen and makes ammonia more toxic — watch fish closely.', { affects: ['ammonia', 'oxygen'] }));
    else out.push(mk('waterTemp', 'healthy', 'A workable temperature; read the other numbers in light of it.', { affects: ['ammonia', 'oxygen'] }));
    return out;
  }

  // ================= Confidence model =================
  function computeConfidence(input) {
    var basedOn = [], missing = [], reasons = [];
    var g = estimateGallons(input);
    var fields = [
      { k: 'pond volume', has: !!g.gallons }, { k: 'fish load', has: !!(input.fishLoad) },
      { k: 'season', has: !!(input.season) }, { k: 'pH', has: num(input.ph) !== null },
      { k: 'ammonia', has: num(input.ammonia) !== null }, { k: 'nitrite', has: num(input.nitrite) !== null },
      { k: 'KH', has: num(input.kh) !== null }, { k: 'dissolved oxygen', has: num(input.oxygen) !== null },
      { k: 'water temperature', has: num(input.waterTemp) !== null }
    ];
    fields.forEach(function (f) { (f.has ? basedOn : missing).push(f.k); });
    var waterKeys = ['ph', 'ammonia', 'nitrite', 'nitrate', 'kh', 'oxygen', 'waterTemp'];
    var waterCount = waterKeys.filter(function (k) { return num(input[k]) !== null; }).length;
    var statuses = computeReadingStatuses(input);
    var implausible = statuses.some(function (s) { return s.status === 'unable'; });
    var symptoms = Array.isArray(input.symptoms) ? input.symptoms : (input.symptoms ? [input.symptoms] : []);
    var symptomsNoData = symptoms.length > 0 && waterCount === 0;
    var keyMissing = ['pH', 'ammonia', 'nitrite', 'KH', 'dissolved oxygen', 'water temperature'].filter(function (m) { return missing.indexOf(m) > -1; }).length;
    var downgrades = 0;
    if (!g.gallons) { downgrades++; reasons.push('Pond volume is unknown, so product sizing and urgency are estimates.'); }
    if (keyMissing >= 3) { downgrades++; reasons.push('Several key water readings are missing.'); }
    if (implausible) { downgrades++; reasons.push('At least one entered value looks implausible and should be retested.'); }
    if (!input.testMethod) reasons.push('Test method is unknown, so we can’t weigh how reliable the readings are.');
    if (symptomsNoData) { downgrades += 2; reasons.push('You reported symptoms without water-test data — testing is the responsible first step.'); }
    var level;
    if (waterCount === 0 && !g.gallons) level = 'none';
    else if (downgrades === 0) level = 'high';
    else if (downgrades === 1) level = 'moderate';
    else if (downgrades === 2) level = 'low';
    else level = waterCount >= 4 ? 'low' : 'none';
    return { level: level, basedOn: basedOn, missing: missing, reasons: reasons };
  }

  // ================= Scoring model (transparent, conservative) =================
  function scoreFromStatus(s) { return { healthy: 100, monitor: 72, concerning: 42, critical: 10 }[s]; }

  function worstOf(keys, byKey) {
    var order = ['critical', 'concerning', 'monitor', 'healthy'], worst = null, missing = [];
    keys.forEach(function (k) {
      var s = byKey[k]; if (!s || s.status === 'unable') return;
      if (s.status === 'missing') { missing.push(s.name); return; }
      if (!worst || order.indexOf(s.status) < order.indexOf(worst.status)) worst = s;
    });
    return { worst: worst, missing: missing };
  }
  function avgScore(keys, byKey) {
    var vals = keys.map(function (k) { return byKey[k] && scoreFromStatus(byKey[k].status) != null ? scoreFromStatus(byKey[k].status) : null; }).filter(function (n) { return n !== null; });
    return vals.length ? Math.round(vals.reduce(function (a, b) { return a + b; }, 0) / vals.length) : null;
  }
  function catStatus(score) { return score === null ? 'unable' : score >= 85 ? 'healthy' : score >= 65 ? 'monitor' : score >= 40 ? 'concerning' : 'critical'; }
  function mkCat(name, score, keys, byKey) {
    var w = worstOf(keys, byKey);
    return { name: name, score: score, status: catStatus(score),
      explanation: score === null ? 'No readings entered for this category yet.' : (w.worst ? w.worst.name + ': ' + w.worst.explanation : 'Readings look comfortable.'),
      topConcern: w.worst && w.worst.status !== 'healthy' ? w.worst.name : null, missing: w.missing };
  }
  function fishLoadRisk(input, g) {
    var fish = input.fishLoad || '', missing = [];
    if (!fish) missing.push('fish load');
    if (!g.gallons) missing.push('pond volume');
    if (!fish && !g.gallons) return { name: 'Fish-Load Risk', score: null, status: 'unable', explanation: 'Enter fish load and pond size to gauge stocking risk.', topConcern: null, missing: missing };
    var isKoi = /koi|heavy/i.test(fish), score = 90, concern = null;
    if (fish === 'No fish') score = 100;
    else if (isKoi) { score = 65; concern = 'A heavy/koi load needs strong filtration and aeration'; }
    if (g.gallons && g.gallons < 500 && isKoi) { score = 40; concern = 'Heavy fish load in a small volume raises waste and swings'; }
    if (isKoi && (input.filtration || '').toLowerCase().indexOf('none') > -1) { score = Math.min(score, 35); concern = 'Heavy fish load with no filtration'; }
    return { name: 'Fish-Load Risk', score: score, status: catStatus(score), explanation: concern ? concern + '.' : 'Stocking looks reasonable for the volume.', topConcern: concern, missing: missing };
  }
  function seasonalReadiness(input) {
    var season = normalizeSeason(input.season);
    if (!season) return { name: 'Seasonal Readiness', score: null, status: 'unable', explanation: 'Tell us the season to tailor maintenance timing.', topConcern: null, missing: ['season'] };
    var map = { Spring: 'Spring is startup season — restart beneficial bacteria as water warms.',
      Summer: 'Summer means heat and oxygen demand — keep aeration steady and feed lightly.',
      Fall: 'Fall is prep season — cold-water bacteria and leaf/debris control matter now.',
      Winter: 'Winter is low-activity — keep a gas-exchange hole open and stop feeding when cold.' };
    return { name: 'Seasonal Readiness', score: 80, status: 'monitor', explanation: map[season], topConcern: null, missing: [] };
  }
  function computeScore(input, statuses) {
    statuses = statuses || computeReadingStatuses(input);
    var byKey = {}; statuses.forEach(function (s) { byKey[s.key] = s; });
    var g = estimateGallons(input);
    var chem = avgScore(['ph', 'ammonia', 'nitrite', 'nitrate', 'kh'], byKey);
    var oxy = avgScore(['oxygen', 'waterTemp'], byKey);
    var bio = avgScore(['ammonia', 'nitrite'], byKey);
    var flr = fishLoadRisk(input, g), seas = seasonalReadiness(input);
    var enteredKeys = ['ph', 'ammonia', 'nitrite', 'nitrate', 'kh', 'oxygen', 'waterTemp'].filter(function (k) { return num(input[k]) !== null; });
    var completeness = Math.round((enteredKeys.length / 7) * 100);
    var categories = [
      mkCat('Water Chemistry', chem, ['ph', 'ammonia', 'nitrite', 'nitrate', 'kh'], byKey),
      mkCat('Oxygen & Temperature', oxy, ['oxygen', 'waterTemp'], byKey),
      mkCat('Biological Filtration', bio, ['ammonia', 'nitrite'], byKey),
      flr, seas,
      { name: 'Data Completeness', score: completeness, status: completeness >= 70 ? 'healthy' : completeness >= 40 ? 'monitor' : 'concerning',
        explanation: enteredKeys.length + ' of 7 core readings entered' + (g.gallons ? ', plus pond volume.' : '; pond volume not set.'), topConcern: null, missing: [] }
    ];
    var weighted = [], wsum = 0;
    function addW(sc, w) { if (sc !== null) { weighted.push(sc * w); wsum += w; } }
    addW(chem, 3); addW(oxy, 2.5); addW(bio, 1.5); addW(flr.score, 1); addW(seas.score, 0.5);
    var hasSafety = ['ph', 'ammonia', 'nitrite'].some(function (k) { return num(input[k]) !== null; });
    var enoughInfo = (enteredKeys.length >= 3 || (g.gallons && enteredKeys.length >= 2)) && hasSafety;
    var overall = null;
    if (enoughInfo && wsum > 0) {
      overall = Math.round(weighted.reduce(function (a, b) { return a + b; }, 0) / wsum);
      var amm = num(input.ammonia), nit = num(input.nitrite), ox = byKey.oxygen;
      if (statuses.some(function (s) { return s.status === 'critical'; })) overall = Math.min(overall, 45);
      if ((amm !== null && amm > 0) || (nit !== null && nit > 0)) overall = Math.min(overall, 58);
      if (ox && (ox.status === 'concerning' || ox.status === 'critical')) overall = Math.min(overall, 52);
      overall = Math.max(0, Math.min(100, overall));
    }
    var label = overall === null ? 'Not enough information for a reliable score'
      : overall >= 85 ? 'Generally Healthy' : overall >= 70 ? 'Mostly Healthy — a few things to watch'
      : overall >= 50 ? 'Needs Attention' : 'Action Needed';
    return { overall: overall, label: label, enoughInfo: enoughInfo, categories: categories };
  }

  // ================= Urgency + prioritized steps =================
  function detectUrgency(input, statuses) {
    statuses = statuses || computeReadingStatuses(input);
    var reasons = [];
    var symptoms = Array.isArray(input.symptoms) ? input.symptoms : (input.symptoms ? [input.symptoms] : []);
    if (symptoms.indexOf('Fish gasping') > -1) reasons.push('Fish gasping at the surface');
    if (symptoms.indexOf('Dead fish') > -1) reasons.push('Sudden fish losses');
    statuses.forEach(function (s) { if (s.status === 'critical') reasons.push(s.name + ' is at a critical level'); });
    var ox = statuses.filter(function (s) { return s.key === 'oxygen'; })[0];
    if (ox && ox.status === 'concerning') reasons.push('Low dissolved oxygen');
    if (input.chlorine === 'Present / suspected') reasons.push('Chlorine/chloramine suspected');
    var seen = {}, uniq = reasons.filter(function (r) { return seen[r] ? false : (seen[r] = true); });
    return { urgent: uniq.length > 0, reasons: uniq };
  }
  function prioritizeSteps(analysis) {
    var doFirst = [], thenMonitor = [], longTerm = [];
    (analysis.advisories || []).forEach(function (a) {
      var step = { title: a.title, points: a.points, tier: a.tier };
      if (a.tier === 'CRITICAL' || a.tier === 'URGENT') doFirst.push(step);
      else if (a.tier === 'WARNING') thenMonitor.push(step);
      else longTerm.push(step);
    });
    return { doFirst: doFirst, thenMonitor: thenMonitor, longTerm: longTerm };
  }

  // ================= Full report composition =================
  function buildReport(input) {
    var analysis = analyze(input);
    var statuses = computeReadingStatuses(input);
    var score = computeScore(input, statuses);
    var confidence = computeConfidence(input);
    var urgency = detectUrgency(input, statuses);
    var steps = prioritizeSteps(analysis);
    var subscription = buildSubscription(input, analysis);
    return { analysis: analysis, statuses: statuses, score: score, confidence: confidence,
      urgency: urgency, steps: steps, neededCategories: analysis.neededCategories,
      subscription: subscription, gallons: analysis.gallons, gallonsEstimated: analysis.gallonsEstimated };
  }

  return { analyze: analyze, buildReport: buildReport, buildSubscription: buildSubscription,
    computeReadingStatuses: computeReadingStatuses, computeScore: computeScore, computeConfidence: computeConfidence,
    detectUrgency: detectUrgency, prioritizeSteps: prioritizeSteps, recommendCarePath: recommendCarePath,
    RULES: RULES, CATEGORIES: CATEGORIES, READINGS: READINGS, estimateGallons: estimateGallons, unIonizedFraction: unIonizedFraction };
});
